import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { Exchange } from '../../../types/exchange';
import useWidthBreakpoint from '../../../hooks/useWidthBreakpoint';
import { setUserId as setStoreUserId } from '../../../store/slices/user';
import {
  useCreateUserMutation,
  useCheckExchangeConnectionMutation,
  useAddExchangeConnectionMutation,
  useEditUserOnboardingStepMutation,
  useEditAreNotificationsEnabledMutation,
  useGetTopBotsQuery,
} from '../../../store/services/api';
import { HINTS } from './data';
import Rail from './Rail';
import HorizontalRail from './HorizontalRail';
import NavRow from './NavRow';
import Account, { AccountValues } from './steps/Account';
import ExchangeStep, { ExchangeValues } from './steps/Exchange';
import Information from './steps/Information';
import Pages from './steps/Pages';
import Complete, { TopPerformer } from './steps/Complete';
import ContentTransition from './animations/ContentTransition';

// Account (step 0) and exchange (step 1) commit to the backend, so Previous is
// blocked on the steps after them (Exchange + Info).
const LOCK_IRREVERSIBLE_STEPS = true;

// Finish (item 5): how long the cascade plays before navigating to the app.
const FINISH_MS = 1100;

// Backend onboardingStep for a given step index (Account=0 → 1, … Complete=4 →
// 5). Login resume maps the other way: step = onboardingStep − 1.
const backendStep = (stepIndex: number) => stepIndex + 1;

type TabletLayout = 'rail' | 'top';

const Onboarding: React.FC<{
  onBackToLogin?: () => void;
  // Login-resume: a partially-onboarded user logs in mid-flow.
  initialUserId?: number | null;
  initialStep?: number;
}> = ({ onBackToLogin, initialUserId = null, initialStep = 0 }) => {
  const dispatch = useDispatch();
  const { breakpoint } = useWidthBreakpoint();

  const [step, setStep] = useState(initialStep);
  const [userId, setUserId] = useState<number | null>(initialUserId);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const contentRef = useRef<HTMLDivElement>(null);

  // Tablet (md) layout follows orientation: landscape → vertical rail,
  // portrait → top stepper.
  const [portrait, setPortrait] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(orientation: portrait)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(orientation: portrait)');
    const handler = (e: MediaQueryListEvent) => setPortrait(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const [account, setAccount] = useState<AccountValues>({
    email: '',
    username: '',
    password: '',
    repeatPassword: '',
    termsAccepted: false,
  });
  const [exchange, setExchange] = useState<ExchangeValues>({
    selected: Exchange.Binance,
    key: '',
    secret: '',
    passphrase: '',
    status: 'pending',
  });
  const [emailsEnabled, setEmailsEnabled] = useState(true);

  // Login-resume: a partially-onboarded user logged in — jump to their step.
  // (This component stays mounted under the transition, so react to the prop.)
  useEffect(() => {
    if (initialUserId != null) {
      setUserId(initialUserId);
      setStep(initialStep);
    }
  }, [initialUserId, initialStep]);

  // ── Backend calls ───────────────────────────────────────────────
  const [createUser, { isLoading: creatingUser }] = useCreateUserMutation();
  const [checkConnection] = useCheckExchangeConnectionMutation();
  const [addConnection, { isLoading: addingConnection }] =
    useAddExchangeConnectionMutation();
  const [editOnboardingStep] = useEditUserOnboardingStepMutation();
  const [editNotifications] = useEditAreNotificationsEnabledMutation();

  // Top performers for the completion screen — top assets sorted by SPC score
  // (the backend sorts on score under the 'stableGrowth' key). Only fetch on the
  // final step, not earlier in the flow.
  const { data: topData } = useGetTopBotsQuery(
    { userId, query: undefined, sortBy: 'stableGrowth' },
    { skip: !userId || step !== 4 },
  );
  const performers: TopPerformer[] = useMemo(() => {
    const assets = topData?.assets;
    if (!assets) return [];
    const best = Object.values(assets)
      .map((arr) => arr?.[0])
      .filter((a): a is NonNullable<typeof a> => Boolean(a));
    best.sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
    return best.slice(0, 4).map((a, i) => ({
      rank: String(i + 1).padStart(2, '0'),
      pair: `${a.baseAsset} / ${a.quoteAsset}`,
      exch: a.exchange,
    }));
  }, [topData]);

  const onAccountChange = useCallback(
    <K extends keyof AccountValues>(key: K, value: AccountValues[K]) => {
      setError(undefined);
      setAccount((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Selecting a different exchange clears credentials + resets the test status.
  const onSelectExchange = useCallback((e: Exchange) => {
    setError(undefined);
    setExchange({
      selected: e,
      key: '',
      secret: '',
      passphrase: '',
      status: 'pending',
    });
  }, []);

  // Editing any credential invalidates a prior successful test.
  const onExchangeField = useCallback(
    (key: 'key' | 'secret' | 'passphrase', value: string) => {
      setError(undefined);
      setExchange((prev) => ({ ...prev, [key]: value, status: 'pending' }));
    },
    [],
  );

  const onTestConnection = useCallback(async () => {
    if (!exchange.key || !exchange.secret) return;
    setError(undefined);
    setExchange((prev) => ({ ...prev, status: 'testing' }));
    const res = await checkConnection({
      exchange: exchange.selected,
      key: exchange.key,
      secret: exchange.secret,
      passphrase: exchange.passphrase || undefined,
    });
    if ('data' in res && res.data?.success) {
      setExchange((prev) => ({ ...prev, status: 'ok' }));
    } else {
      setExchange((prev) => ({ ...prev, status: 'fail' }));
      setError(
        ('data' in res && res.data?.errorMessage) || 'Connection test failed.',
      );
    }
  }, [
    checkConnection,
    exchange.key,
    exchange.secret,
    exchange.selected,
    exchange.passphrase,
  ]);

  const onToggleEmails = useCallback(
    (value: boolean) => {
      setEmailsEnabled(value);
      if (userId) {
        editNotifications({ userId, areNotificationsEnabled: value });
      }
    },
    [editNotifications, userId],
  );

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0:
        return (
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account.email) &&
          !!account.username &&
          !!account.password &&
          !!account.repeatPassword &&
          account.password === account.repeatPassword &&
          account.termsAccepted
        );
      case 1:
        return exchange.status === 'ok';
      default:
        return true;
    }
  }, [step, account, exchange.status]);

  const busy = creatingUser || addingConnection;

  const handleNext = useCallback(async () => {
    if (!canAdvance || busy) return;
    setError(undefined);

    // Step 0 — create the account (skip if already created, e.g. navigated back).
    if (step === 0) {
      if (userId) {
        editOnboardingStep({ userId, onboardingStep: backendStep(1) });
        setStep(1);
        return;
      }
      const res = await createUser({
        email: account.email,
        username: account.username,
        password: account.password,
      });
      if ('data' in res && res.data?.success) {
        const newId = res.data.userId;
        setUserId(newId);
        editOnboardingStep({ userId: newId, onboardingStep: backendStep(1) });
        setStep(1);
      } else {
        setError(
          ('data' in res && res.data?.error) || 'Could not create the account.',
        );
      }
      return;
    }

    // Step 1 — commit the (already-tested) exchange connection.
    if (step === 1) {
      if (!userId) {
        setError('Missing account — please restart onboarding.');
        return;
      }
      const res = await addConnection({
        userId,
        exchange: exchange.selected,
        key: exchange.key,
        secret: exchange.secret,
        passphrase: exchange.passphrase || undefined,
      });
      if ('data' in res && res.data?.success) {
        editOnboardingStep({ userId, onboardingStep: backendStep(2) });
        setStep(2);
      } else {
        setError(
          ('data' in res && res.data?.errorMessage) ||
            'Could not add the exchange connection.',
        );
      }
      return;
    }

    // Steps 2/3 — informational; just persist progress.
    if (step === 2 || step === 3) {
      if (userId) {
        editOnboardingStep({ userId, onboardingStep: backendStep(step + 1) });
      }
      setStep((s) => Math.min(s + 1, 4));
      return;
    }

    // Step 4 — mark complete, play the finish animation, then navigate.
    if (step === 4) {
      if (userId) editOnboardingStep({ userId, onboardingStep: null });
      setCompleting(true);
    }
  }, [
    canAdvance,
    busy,
    step,
    userId,
    account,
    exchange,
    createUser,
    addConnection,
    editOnboardingStep,
  ]);

  const handlePrev = useCallback(() => {
    if (step === 0) {
      onBackToLogin?.();
      return;
    }
    setError(undefined);
    setStep((s) => Math.max(s - 1, 0));
  }, [step, onBackToLogin]);

  // After the cascade plays, enter the app (navigate to the dashboard).
  useEffect(() => {
    if (!completing) return undefined;
    const t = setTimeout(() => {
      if (userId) dispatch(setStoreUserId(userId));
      else console.warn('[Onboarding] finish without a userId');
    }, FINISH_MS);
    return () => clearTimeout(t);
  }, [completing, userId, dispatch]);

  // Reset the content scroll to the top whenever the step changes.
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [step]);

  const prevDisabled = LOCK_IRREVERSIBLE_STEPS && (step === 1 || step === 2);
  const railStep = completing ? 5 : step;

  // ── Responsive layout ──────────────────────────────────────────
  const phone = breakpoint === 'sm';
  const layout: TabletLayout = phone
    ? 'top'
    : breakpoint === 'md'
    ? portrait
      ? 'top'
      : 'rail'
    : 'rail';
  const railWidth = breakpoint === 'lg' ? 420 : 340;
  const narrow = phone;
  const fixedHeight = !phone;
  const tabletRail = breakpoint === 'md' && layout === 'rail';

  let stepContent: React.ReactNode = null;
  switch (step) {
    case 0:
      stepContent = (
        <Account
          values={account}
          onChange={onAccountChange}
          phone={phone}
          error={error}
        />
      );
      break;
    case 1:
      stepContent = (
        <ExchangeStep
          values={exchange}
          onSelect={onSelectExchange}
          onField={onExchangeField}
          onTest={onTestConnection}
          error={error}
          phone={phone}
          narrow={narrow}
        />
      );
      break;
    case 2:
      stepContent = (
        <Information
          emailsEnabled={emailsEnabled}
          onToggleEmails={onToggleEmails}
          phone={phone}
          narrow={narrow}
          tabletRail={tabletRail}
        />
      );
      break;
    case 3:
      stepContent = (
        <Pages phone={phone} narrow={narrow} tabletRail={tabletRail} />
      );
      break;
    case 4:
      stepContent = (
        <Complete
          summary={{
            email: account.email,
            exchangeName: exchange.selected,
            emailsEnabled,
          }}
          performers={performers}
          phone={phone}
          narrow={narrow}
          tabletRail={tabletRail}
        />
      );
      break;
    default:
      stepContent = null;
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: '#000',
        color: '#fff',
        fontFamily: 'Kanit, sans-serif',
        display: 'flex',
        flexDirection: layout === 'top' ? 'column' : 'row',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {layout === 'top' ? (
        <HorizontalRail step={railStep} phone={phone} />
      ) : (
        <Rail step={railStep} width={railWidth} />
      )}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          ref={contentRef}
          style={{
            flex: 1,
            // Tablet/desktop: flex fill — title stays fixed, body scrolls
            // internally. Phone: block flow so the whole page scrolls naturally.
            display: fixedHeight ? 'flex' : 'block',
            flexDirection: 'column',
            minHeight: 0,
            overflowY: fixedHeight ? 'hidden' : 'auto',
          }}
        >
          <ContentTransition step={step}>{stepContent}</ContentTransition>
        </div>
        {/* Keep the nav's space reserved during the finish so the completion
            content doesn't shift; just hide it (also blocks interaction). */}
        <div
          style={{
            flexShrink: 0,
            visibility: completing ? 'hidden' : 'visible',
          }}
        >
          <NavRow
            step={step}
            canAdvance={canAdvance}
            prevDisabled={prevDisabled}
            loading={busy}
            hint={HINTS[step]}
            phone={phone}
            showHint={breakpoint === 'lg'}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </div>
      </div>

      {/* Finish cascade (item 5): green wash sweeps over the rail region. */}
      {completing && (
        <motion.div
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          style={
            layout === 'top'
              ? {
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  height: '100%',
                  transformOrigin: 'top',
                  background:
                    'linear-gradient(to bottom, rgba(0,176,97,.35), rgba(0,176,97,.02))',
                  pointerEvents: 'none',
                }
              : {
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: railWidth,
                  transformOrigin: 'bottom',
                  background:
                    'linear-gradient(to top, rgba(0,176,97,.35), rgba(0,176,97,.05))',
                  pointerEvents: 'none',
                }
          }
        />
      )}
    </div>
  );
};

export default Onboarding;
